'use client';

import { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface SheikhNotification {
  id: string;
  questionId: string;
  seniorName: string;
  specialisation: string;
  questionText: string;
  questionType: 'text' | 'audio';
  receivedAt: string;
  read: boolean;
}

export function useSheikhNotifications(scholarId?: string) {
  const [notifications, setNotifications] = useState<SheikhNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const supabase = createClient();

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const markRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const dismissNotification = useCallback((notificationId: string) => {
    setNotifications((prev) => {
      const updated = prev.filter((n) => n.id !== notificationId);
      setUnreadCount(updated.filter((n) => !n.read).length);
      return updated;
    });
  }, []);

  useEffect(() => {
    if (!scholarId) return;

    // Subscribe to new senior_questions assigned to this scholar
    const channel = supabase
      .channel(`sheikh-notifications-${scholarId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'senior_questions',
          filter: `scholar_id=eq.${scholarId}`,
        },
        (payload) => {
          const row = payload.new as {
            id: string;
            question_text?: string;
            question_type?: string;
            specialisation?: string;
            created_at?: string;
            senior_id?: string;
          };

          const newNotification: SheikhNotification = {
            id: `notif-${row.id}-${Date.now()}`,
            questionId: row.id,
            seniorName: 'A Senior Member',
            specialisation: row.specialisation || 'General',
            questionText: row.question_text || 'New audio question received',
            questionType: (row.question_type as 'text' | 'audio') || 'text',
            receivedAt: row.created_at || new Date().toISOString(),
            read: false,
          };

          setNotifications((prev) => [newNotification, ...prev].slice(0, 20));
          setUnreadCount((prev) => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [scholarId]);

  return {
    notifications,
    unreadCount,
    markAllRead,
    markRead,
    dismissNotification,
  };
}
